import React, { createContext, useState, useEffect, useContext } from 'react';
// Nếu muốn dùng toast trong context thì uncomment dòng dưới, 
// nhưng thường toast nên gọi ở UI (Page) để tránh lỗi render cycle.
// import { toast } from 'react-toastify';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const localData = localStorage.getItem('cartItems');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // --- HELPER: TÍNH GIÁ ---
  const calculateFinalPrice = (item) => {
    const originalPrice = parseFloat(item.price || 0);
    const salePrice = parseFloat(item.sale_price || 0);
    const discountPercent = parseFloat(item.discount_percentage || 0);

    if (salePrice > 0 && salePrice < originalPrice) return salePrice;
    if (discountPercent > 0) return originalPrice * (1 - discountPercent / 100);
    return originalPrice;
  };

  // --- 1. HÀM THÊM VÀO GIỎ (ĐÃ SỬA LOGIC KHO) ---
  const addToCart = (product, quantity) => {
    // [FIX QUAN TRỌNG] Xử lý Tồn Kho An Toàn
    // Nếu API không trả về stock_quantity hoặc bị null -> Mặc định là 999 để khách vẫn mua được.
    // Nếu API trả về số 0 -> Thì mới coi là hết hàng.
    let currentStock = 999; 
    if (product.stock_quantity !== undefined && product.stock_quantity !== null) {
        currentStock = parseInt(product.stock_quantity);
    }

    // Nếu thực sự hết hàng (stock = 0), không cho thêm
    if (currentStock <= 0) return;

    // Xử lý ảnh thumbnail
    let thumbnailUrl = '';
    if (product.images && product.images.length > 0) {
        thumbnailUrl = product.images[0].file_path; 
    } else if (product.thumbnail_url) {
        thumbnailUrl = product.thumbnail_url;
    }

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);

      if (existingItem) {
        // [LOGIC CỘNG DỒN]
        let newQuantity = existingItem.quantity + quantity;

        // Nếu vượt quá kho -> Lấy max kho
        if (newQuantity > currentStock) {
            newQuantity = currentStock;
        }

        return prevItems.map(item =>
          item.id === product.id
            ? { 
                ...item, 
                quantity: newQuantity,
                // Luôn cập nhật lại thông tin mới nhất từ sản phẩm đang xem
                stock_quantity: currentStock, 
                price: product.price,
                sale_price: product.sale_price
              }
            : item
        );
      } else {
        const finalQuantity = quantity > currentStock ? currentStock : quantity;

        const newItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          sale_price: product.sale_price, 
          discount_percentage: product.discount_percentage,
          quantity: finalQuantity,
          stock_quantity: currentStock,
          thumbnail_url: thumbnailUrl
        };
        return [...prevItems, newItem];
      }
    });
  };

  // --- 2. HÀM CẬP NHẬT SỐ LƯỢNG (+/-) ---
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.id === productId) {
            // Lấy tồn kho đã lưu trong item (hoặc mặc định an toàn)
            const stock = item.stock_quantity !== undefined ? parseInt(item.stock_quantity) : 999;
            
            // Chặn không cho vượt quá kho
            if (newQuantity > stock) {
                return { ...item, quantity: stock };
            }
            return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  // 3. Xóa sản phẩm
  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };
  
  // 4. Xóa sạch giỏ
  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
  };

  // 5. Tổng số lượng item
  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // 6. Tổng tiền
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
        return total + (calculateFinalPrice(item) * item.quantity);
    }, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartItemCount,
        getCartTotal,
        calculateFinalPrice
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};